/**
 * `useOptionalLot` / `useOptionalLotsConnection` — one lot's admin lot-state
 * fold, and the stream's connection, read from whichever
 * `<RealtimeLotsProvider>` is above, or nothing when none is (BL-2253).
 *
 * Promoted out of `useLotStateForRecord` (where it was the private `useLotFold`,
 * BL-1542) when the Lot Edit drawer became its second reader: one nullable fold
 * reader, not two (sharing.md § 4).
 *
 * WHY NOT `useLot`. `useLot` calls `useLotStateStore()`, which THROWS without a
 * provider — and throws before it looks at `lotId`, so `useLot(null)` is not a
 * safe no-op either. A reader that can render outside a provider (the opening-bid
 * notice on the lot CREATE door; the drawer header on a home lot, which has no
 * auction and so no stream) needs the nullable read, and it keeps the hook
 * callable unconditionally (rule #7).
 *
 * @sideEffect none. It reads a store someone else owns.
 */
'use client';

import { useCallback, useMemo, useSyncExternalStore } from 'react';

import type { AdminLotStateDeltaDecoded } from '../../mappers/realtime/admin-lot-state';
import { useOptionalLotStateStore } from '../../components/realtime/RealtimeLotsProvider';
import { DISCONNECTED_LOT_STATE, type LotStateConnection } from '../../stores/realtime/lot-state-store';

/** Stable empty fold — a fresh Map each call would tear `useSyncExternalStore`. */
const EMPTY_LOT_MAP: ReadonlyMap<string, AdminLotStateDeltaDecoded> = new Map();

/**
 * One lot off the nearest lot-state fold, tolerating an absent provider.
 *
 * The `subscribe` callback branches, which is legal — it is a callback, not a
 * hook — so the null-store case attaches no listener.
 *
 * @param lotId - The lot to read; `null` yields `undefined`.
 * @returns The lot's decoded delta, or `undefined` until it lands or when no
 *   provider is mounted.
 */
export function useOptionalLot(lotId: string | null): AdminLotStateDeltaDecoded | undefined {
    const store = useOptionalLotStateStore();
    const subscribe = useCallback(
        (listener: () => void) => (store ? store.subscribe(listener) : () => {}),
        [store],
    );
    const getSnapshot = useCallback(() => (store ? store.getLots() : EMPTY_LOT_MAP), [store]);
    const map = useSyncExternalStore(subscribe, getSnapshot, () => EMPTY_LOT_MAP);
    return useMemo(() => (lotId ? map.get(lotId) : undefined), [map, lotId]);
}

/**
 * The nearest lot-state stream's connection, or `null` when no provider is
 * mounted — so a reader can tell "no stream here" from "still connecting".
 *
 * @returns The connection state, or `null` without a provider.
 */
export function useOptionalLotsConnection(): LotStateConnection | null {
    const store = useOptionalLotStateStore();
    const subscribe = useCallback(
        (listener: () => void) => (store ? store.subscribe(listener) : () => {}),
        [store],
    );
    const getSnapshot = useCallback(
        () => (store ? store.getConnection() : DISCONNECTED_LOT_STATE),
        [store],
    );
    const connection = useSyncExternalStore(subscribe, getSnapshot, () => DISCONNECTED_LOT_STATE);
    return store ? connection : null;
}
